import type { ReactNode } from "react";
import { NavLink } from "react-router-dom";
import AppHeader from "@/components/feature/AppHeader";
import ScrollFade from "@/components/base/ScrollFade";

const adminNavItems = [
  { to: "/app/admin", label: "Resumen", icon: "ri-dashboard-3-line", end: true },
  { to: "/app/admin/usuarios", label: "Usuarios", icon: "ri-team-line", end: false },
  { to: "/app/admin/cumplimiento", label: "Cumplimiento", icon: "ri-shield-check-line", end: false },
  { to: "/app/admin/afiliados", label: "Afiliados", icon: "ri-team-line", end: false },
  { to: "/app/admin/comunidad", label: "Comunidad y retos", icon: "ri-community-line", end: false },
  { to: "/app/admin/eventos", label: "Calendario", icon: "ri-calendar-event-line", end: false },
  { to: "/app/admin/webinar", label: "Webinar", icon: "ri-slideshow-3-line", end: false },
  { to: "/app/admin/whatsapp", label: "Agente WhatsApp", icon: "ri-robot-2-line", end: false },
  { to: "/app/admin/notificaciones", label: "Avisos WhatsApp", icon: "ri-whatsapp-line", end: false },
];

type AdminShellProps = {
  children: ReactNode;
  title: string;
  description: string;
};

/**
 * Layout propio del Panel Administrador: header compartido con el resto de
 * la app + navegación del panel (barra lateral oscura en escritorio, tabs
 * deslizables en móvil). No usa el BottomNav de la app — el admin no
 * navega por ahí mientras está en este panel.
 */
export default function AdminShell({ children, title, description }: AdminShellProps) {
  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <AppHeader variant="onLight" showBack />

      <div className="flex-1 flex flex-col md:flex-row max-w-7xl w-full mx-auto">
        {/* Sidebar — escritorio */}
        <aside className="hidden md:flex md:w-60 flex-shrink-0 flex-col gap-1 bg-background-950 px-3 py-6 md:min-h-[calc(100vh-5rem)]">
          <div className="px-3 pb-4 mb-2 border-b border-accent-800/40">
            <span className="inline-flex items-center gap-2 text-foreground-100 font-heading font-bold text-sm">
              <i className="ri-shield-star-line text-primary-400 text-lg"></i> Panel Administrador
            </span>
          </div>
          {adminNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                [
                  "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold cursor-pointer transition-colors",
                  isActive
                    ? "bg-primary-500/15 text-primary-300"
                    : "text-foreground-300 hover:bg-background-900 hover:text-foreground-100",
                ].join(" ")
              }
            >
              <i className={`${item.icon} text-lg`}></i>
              {item.label}
            </NavLink>
          ))}
        </aside>

        {/* Tabs — móvil */}
        <div className="md:hidden sticky top-0 z-10 bg-background-50 border-b border-background-200 vv-safe-x">
          <ScrollFade containerClassName="flex gap-2 overflow-x-auto px-4 py-3 vv-scrollbar-hidden" ariaLabel="Secciones del panel administrador">
            {adminNavItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  [
                    "flex-shrink-0 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors",
                    isActive
                      ? "bg-primary-500 text-background-950"
                      : "bg-background-100 text-foreground-600 border border-background-200",
                  ].join(" ")
                }
              >
                <i className={`${item.icon}`}></i>
                {item.label}
              </NavLink>
            ))}
          </ScrollFade>
        </div>

        <main className="flex-1 min-w-0 px-4 md:px-8 py-6 md:py-8">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-foreground-950">{title}</h1>
            <p className="text-foreground-600 text-sm mt-1">{description}</p>
          </div>
          <div className="mt-6">{children}</div>
        </main>
      </div>
    </div>
  );
}
